import React, { useEffect, useState } from 'react'
import { authPut } from '../../../../../shared/sso-workspace/auth-api'
import { ConfirmDialog } from '../../../../../shared/sso-workspace/confirm-dialog'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'

// auth's cap (org-sso MaxTestAllowlist): checked here first so the person
// hears it before a request, and again by auth.
export const MAX_TEST_ADDRESSES = 50

// The organization alert recipients' rule (org-sso-recipient.js), which auth
// applies to test addresses too.
const EMAIL = /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/

export const connectionName = connection => connection.displayName || connection.providerName

// auth sends a connection's test addresses only when the organization runs
// it itself (selfServe: its own, on EducationXR's shared app); an older auth
// sends none, and then there is nothing to edit.
export const isTestable = connection => Boolean(connection && connection.selfServe !== false && connection.owned && Array.isArray(connection.testAllowlist))

// Whether a connection sends anyone to it right now: switched on, with a
// domain bound (any organization's: auth's boundDomainCount, else the
// domains this page knows). Only then does starting or ending a test change
// how people sign in, and only then does the page ask first.
export const inUse = connection => {
  if (!connection.active) return false
  if (typeof connection.boundDomainCount === 'number') return connection.boundDomainCount > 0
  return (connection.domains || []).length > 0
}

export const domainsText = connection => {
  const domains = (connection.domains || []).map(d => d.domain).filter(Boolean)
  return domains.length ? domains.join(', ') : 'its domains'
}

// auth's refusals on the organization's connection routes, in its own words:
// 400 (a bad list), 403 (not the organization's to change) and 409 (Require
// SSO is on for a domain this would leave with no connection routing it:
// "Turn off Require SSO for acme.org first."). Anything else -- a 5xx, a
// network failure -- gets the page's sentence.
const REFUSED = [400, 403, 409]
export const refusal = (err, fallback) =>
  err && REFUSED.includes(err.status) && err.data && typeof err.data.message === 'string' && err.data.message
    ? err.data.message
    : fallback

export const testingLine = count => count === 0
  ? null
  : count === 1 ? 'Testing: only this person is sent to it.' : `Testing: only these ${count} people are sent to it.`

// The pilot, step by step, in plain words: the Testing section's
// explanation on the organization's connection page.
export const TestingSteps = () =>
  <ol className="sw-hint ps-3 mb-0 d-grid gap-1" data-testid="testing-steps">
    <li>Add the people who will try it first.</li>
    <li>Bind a verified domain to this connection on your Authentication page. A connection waiting for its first domain switches on when it is bound; while this list has addresses, only these people are sent to it.</li>
    <li>They sign in with their work accounts. Everyone else keeps signing in the way they do now.</li>
    <li>When it works, choose Go live: the list is cleared and everyone at the bound domains is sent here.</li>
  </ol>

// One connection's test addresses (auth's test_allowlist): PUT
// /org-sso/connections/:id/test-allowlist. Every add or remove saves the
// whole list at once. It sits inside the connection page's form, so it is
// no <form> of its own: Enter in the field adds the address.
export const ConnectionTesters = ({ connection, enabled, onChanged }) => {
  const stored = connection.testAllowlist || []
  const [list, setList] = useState(stored)
  const [draft, setDraft] = useState('')
  const [hint, setHint] = useState(null)
  const [error, failure, setError] = useErrorLine()
  const [busy, setBusy] = useState(false)
  // { kind: 'start' | 'end' | 'live', next }: the change waiting for a yes.
  const [pending, setPending] = useState(null)

  // A reload of the connection brings the stored list.
  const storedKey = JSON.stringify(stored)
  useEffect(() => { setList(stored) }, [storedKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const name = connectionName(connection)
  const inputID = `org-sso-test-address-${connection.id}`

  const save = async next => {
    setBusy(true)
    setError(null)
    try {
      const result = await authPut(`org-sso/connections/${connection.id}/test-allowlist`, { testAllowlist: next })
      const saved = result && Array.isArray(result.testAllowlist) ? result.testAllowlist : next
      setList(saved)
      setPending(null)
      onChanged && (await onChanged(saved))
      return true
    } catch (err) {
      setError(refusal(err, "The test addresses couldn't be saved."), err)
      return false
    } finally {
      setBusy(false)
    }
  }

  const handleAdd = async () => {
    setError(null)

    const address = draft.trim().toLowerCase()
    if (!address) { setHint("Enter the person's email address."); return }
    if (!EMAIL.test(address)) { setHint('Enter one full email address, like name@example.org.'); return }
    if (list.includes(address)) { setHint('That address is already on the list.'); return }
    if (list.length >= MAX_TEST_ADDRESSES) { setHint(`A connection can have at most ${MAX_TEST_ADDRESSES} test addresses.`); return }
    setHint(null)

    const next = [...list, address]
    if (list.length === 0 && inUse(connection)) { setPending({ kind: 'start', next }); return }
    if (await save(next)) setDraft('')
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter') { e.preventDefault(); handleAdd() }
  }

  const handleRemove = address => {
    const next = list.filter(a => a !== address)
    if (next.length === 0 && inUse(connection)) { setError(null); setPending({ kind: 'end', next }); return }
    save(next)
  }

  const handleConfirm = async () => {
    const starting = pending.kind === 'start'
    if (await save(pending.next) && starting) setDraft('')
  }

  const dialog = pending && {
    start: {
      title: `Test ${name} with a few people?`,
      body: `Only the people on this list will be sent to ${name}. Everyone else at ${domainsText(connection)} stops being sent to it and signs in another way, such as with a password ("Forgot password?" on the sign-in page sets one).`,
      confirm: 'Start testing'
    },
    end: {
      title: `Send everyone to ${name}?`,
      body: `With no test addresses, everyone at ${domainsText(connection)} is sent to ${name} when they sign in.`,
      confirm: 'Send everyone'
    },
    live: {
      title: `Go live with ${name}?`,
      body: connection.active
        ? `The test list is cleared, and everyone at ${domainsText(connection)} will now be sent to ${name} when they sign in.`
        : `The test list is cleared. ${name} is switched off, so nobody is sent to it until you switch it on; then everyone at ${domainsText(connection)} is.`,
      confirm: 'Go live'
    }
  }[pending.kind]

  return (
    <div className="d-grid gap-2" data-testid={`testing-${connection.id}`}>
      <span className="sw-hint d-block" role="status">
        {testingLine(list.length) || `No test addresses. Everyone at ${domainsText(connection)} is sent to it.`}
      </span>

      {list.length > 0 &&
        <ul className="list-unstyled d-flex flex-wrap gap-1 m-0" aria-label={`Test addresses for ${name}`}>
          {list.map(address =>
            <li key={address} className="sw-tag">
              {address}
              {enabled
                ? <button type="button" className="sw-tag-x" disabled={busy} aria-label={`Remove ${address} from the test addresses`}
                  onClick={() => handleRemove(address)}>×</button>
                : <span className="pe-2" />}
            </li>)}
        </ul>}

      {enabled &&
        <div className="d-flex flex-wrap align-items-start gap-2">
          <div className="sw-field flex-grow-1 mb-0" style={{ maxWidth: 320 }}>
            <label className="visually-hidden" htmlFor={inputID}>Add a test address for {name}</label>
            <input id={inputID} type="text" inputMode="email" className="form-control" value={draft} placeholder="name@example.org" autoComplete="off" spellCheck={false}
              disabled={busy} aria-describedby={hint ? `${inputID}-hint` : undefined}
              onKeyDown={handleKeyDown} onChange={e => { setDraft(e.target.value); if (hint) setHint(null) }} />
            {hint && <div id={`${inputID}-hint`} className="sw-required">{hint}</div>}
          </div>
          <button type="button" className="btn sw-btn" disabled={busy} onClick={handleAdd}>Add<span className="visually-hidden"> test address</span></button>
          {list.length > 0 &&
            <button type="button" className="btn sw-btn-primary" disabled={busy} onClick={() => { setError(null); setPending({ kind: 'live', next: [] }) }}>Go live</button>}
        </div>}

      {error && !pending && <p className="text-danger small m-0" role="alert">{error}</p>}
      {error && !pending && <ErrorReference error={failure} className="mt-0" />}

      <ConfirmDialog
        open={Boolean(pending)}
        title={dialog ? dialog.title : ''}
        body={dialog ? dialog.body : ''}
        confirmText={dialog ? dialog.confirm : ''}
        confirmClassName="btn sw-btn-primary"
        busy={busy}
        error={pending ? error : null}
        failure={pending ? failure : null}
        onConfirm={handleConfirm}
        onCancel={() => { setPending(null); setError(null) }}
      />
    </div>
  )
}
